import {
    EDxfEntityKind,
    IDxfBounds,
    IDxfEntity,
    IDxfPoint,
    IDxfVertex,
    arcExtremePoints,
    boundsOfPoints,
    textCorners,
} from './dxf-entities';
import { EDxfLayerKind } from './dxf-layers';

/** One straight piece of a view's drawing. */
export type IDxfSegment = readonly [IDxfPoint, IDxfPoint];

/**
 * A view's drawing reduced to what a dimension asks of it: the vertices it can measure and the
 * straight pieces a dimension number must keep clear of. Arcs count by their chords.
 */
export interface IDxfSketch {
    vertices: IDxfPoint[];
    segments: IDxfSegment[];
    /** The boxes of the labels already set: the view's texts, then each dimension number as it is placed. */
    labels: IDxfBounds[];
    /** The pieces of the visible outline alone, the main line; absent — every segment counts as outline. */
    contour?: IDxfSegment[];
}

function chain(points: readonly IDxfPoint[], closed: boolean): IDxfSegment[] {
    const segments: IDxfSegment[] = [];
    points.forEach((from: IDxfPoint, index: number): void => {
        const to: IDxfPoint | undefined = points.at(index + 1) ?? (closed && points.length > 2 ? points.at(0) : undefined);
        if (to) {
            segments.push([from, to]);
        }
    });
    return segments;
}

function entitySketch(entity: IDxfEntity): IDxfSketch {
    switch (entity.kind) {
        case EDxfEntityKind.Line:
            return { vertices: [entity.from, entity.to], segments: [[entity.from, entity.to]], labels: [] };
        case EDxfEntityKind.Polyline: {
            const points: IDxfPoint[] = entity.vertices.map((vertex: IDxfVertex): IDxfPoint => vertex.at);
            return { vertices: points, segments: chain(points, entity.closed), labels: [] };
        }
        case EDxfEntityKind.Arc: {
            const ends: IDxfPoint[] = arcExtremePoints(entity.center, entity.radius, entity.startDeg, entity.endDeg);
            return { vertices: ends.slice(0, 2), segments: chain(ends, false), labels: [] };
        }
        case EDxfEntityKind.Hatch:
            return { vertices: [...entity.boundary], segments: [], labels: [] };
        case EDxfEntityKind.Text:
            return { vertices: [], segments: [], labels: [boundsOfPoints(textCorners(entity))] };
    }
}

/** The sketch of a view's drawing entities. */
export function sketchOf(entities: readonly IDxfEntity[]): IDxfSketch {
    const sketches: IDxfSketch[] = entities.map(entitySketch);
    const contourLayer: string = `_${EDxfLayerKind.Contour}`;
    return {
        vertices: sketches.flatMap((sketch: IDxfSketch): IDxfPoint[] => sketch.vertices),
        segments: sketches.flatMap((sketch: IDxfSketch): IDxfSegment[] => sketch.segments),
        labels: sketches.flatMap((sketch: IDxfSketch): IDxfBounds[] => sketch.labels),
        contour: sketches.flatMap((sketch: IDxfSketch, index: number): IDxfSegment[] =>
            entities[index].layer.endsWith(contourLayer) ? sketch.segments : []
        ),
    };
}

/** Whether two boxes share any area. */
export function boxesMeet(a: IDxfBounds, b: IDxfBounds): boolean {
    return a.minX < b.maxX && b.minX < a.maxX && a.minY < b.maxY && b.minY < a.maxY;
}

/** Whether a segment crosses or lies in an axis-aligned box (Liang–Barsky clipping). */
export function segmentHitsBox(segment: IDxfSegment, box: IDxfBounds): boolean {
    const [[x0, y0], [x1, y1]]: IDxfSegment = segment;
    const dx: number = x1 - x0;
    const dy: number = y1 - y0;
    const edges: [number, number][] = [
        [-dx, x0 - box.minX],
        [dx, box.maxX - x0],
        [-dy, y0 - box.minY],
        [dy, box.maxY - y0],
    ];
    let enter: number = 0;
    let leave: number = 1;
    for (const [p, q] of edges) {
        if (p === 0) {
            if (q < 0) {
                return false;
            }
            continue;
        }
        const t: number = q / p;
        if (p < 0) {
            enter = Math.max(enter, t);
        } else {
            leave = Math.min(leave, t);
        }
    }
    return enter <= leave;
}
